import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { config } from '../config';
import { ActivationEvidenceSnapshot } from './activation-readiness.service';

/** Snake_case contract from GET /onboarding/v2/activation/evidence */
export interface ActivationEvidenceResponse {
  employee_id: number;
  activity_day?: string | null;
  first_event_at: string | null;
  last_event_at: string | null;
  app_log_session_count: number;
  tracked_activity_minutes: number;
  screenshot_count: number;
  periodic_screenshots_enabled: boolean;
  shift_ended_or_day_ended: boolean;
  daily_insight_recipient_emails: string[];
  plan_or_trial_eligible: boolean;
}

export type ActivationEvidenceApiPartial = Pick<
  ActivationEvidenceSnapshot,
  | 'firstEmployeeId'
  | 'firstEventAt'
  | 'lastEventAt'
  | 'appLogSessionCount'
  | 'trackedActivityMinutes'
  | 'screenshotCount'
  | 'periodicScreenshotsEnabled'
  | 'shiftEndedOrDayEnded'
  | 'dailyInsightRecipientEmails'
  | 'planOrTrialEligible'
  | 'fatalIngestOrAuthError'
  | 'firstScreenshotReceived'
>;

function asIsoOrNull(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? value : null;
}

function asNonNegInt(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return 0;
  }
  return Math.floor(n);
}

function asEmailList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    if (typeof raw !== 'string') {
      continue;
    }
    const email = raw.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      continue;
    }
    const key = email.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(email);
  }
  return out;
}

/**
 * Map API evidence into readiness snapshot fields owned by the backend.
 * Does not set Firebase-owned fields (agentAuthenticated / recentHeartbeat / onlineNow).
 */
export function mapActivationEvidenceResponse(
  response: ActivationEvidenceResponse | null | undefined
): ActivationEvidenceApiPartial {
  const employeeId = asNonNegInt(response?.employee_id);
  const screenshotCount = asNonNegInt(response?.screenshot_count);
  return {
    firstEmployeeId: employeeId > 0 ? employeeId : null,
    firstEventAt: asIsoOrNull(response?.first_event_at),
    lastEventAt: asIsoOrNull(response?.last_event_at),
    appLogSessionCount: asNonNegInt(response?.app_log_session_count),
    trackedActivityMinutes: asNonNegInt(response?.tracked_activity_minutes),
    screenshotCount,
    periodicScreenshotsEnabled: response?.periodic_screenshots_enabled !== false,
    shiftEndedOrDayEnded: !!response?.shift_ended_or_day_ended,
    dailyInsightRecipientEmails: asEmailList(response?.daily_insight_recipient_emails),
    planOrTrialEligible: !!response?.plan_or_trial_eligible,
    fatalIngestOrAuthError: false,
    firstScreenshotReceived: screenshotCount > 0,
  };
}

@Injectable({
  providedIn: 'root',
})
export class ActivationEvidenceService {
  private readonly evidenceUrl =
    config.baseUrl + 'supersee/v1/onboarding/v2/activation/evidence';

  constructor(private http: HttpClient) {}

  /** Read-only fetch; does not mutate journey state. */
  load(employeeId: number): Observable<ActivationEvidenceApiPartial> {
    const id = Number(employeeId);
    if (!Number.isFinite(id) || id <= 0) {
      return throwError(() => new Error('activation_employee_required'));
    }
    return this.http
      .get<ActivationEvidenceResponse>(this.evidenceUrl, {
        params: { employee_id: String(id) },
      })
      .pipe(map((body) => mapActivationEvidenceResponse(body)));
  }
}
